import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Leaf, Mail, Trash2, ListChecks, ShieldCheck, RefreshCw } from "lucide-react";
import { CONTACT_EMAIL, LOGO_URL } from "@/termsData";

const STEPS = [
  "Open the GrowCalc app and sign in.",
  "Tap the Settings gear in the top banner.",
  'Scroll to the bottom and tap "Delete Account".',
  "Confirm the deletion. Your account and data are removed immediately.",
];

const DELETED = [
  "Your account record (email address, display name, password) and active sign-in sessions",
  "All app data: saved products, application logs, reminders, soil pH readings, lawn zones, season archives, equipment maintenance logs, warranties and receipt photos, AI chat and photo-diagnosis history, saved location/ZIP, and preferences",
];

const KEPT = [
  "In-app deletion takes effect immediately; residual copies in encrypted backups and server logs are purged within 30 days.",
  "Subscription purchase records are managed by Google Play / Apple and our billing processor (RevenueCat) and may be retained by them as required for financial record-keeping; we do not retain them ourselves after deletion.",
];

const MailLink = () => (
  <a
    href={`mailto:${CONTACT_EMAIL}`}
    data-testid="delete-email-link"
    className="font-extrabold text-[var(--ld-lime-bright)] underline decoration-[var(--ld-lime)]/40 underline-offset-2 transition-colors hover:text-[var(--ld-lime)]"
  >
    {CONTACT_EMAIL}
  </a>
);

const Bullet = ({ children }) => (
  <li className="flex gap-3 leading-relaxed text-[15px] text-[var(--ld-text)]">
    <span className="mt-[9px] h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--ld-lime)]" />
    <span>{children}</span>
  </li>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="mb-3 flex items-center gap-2 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--ld-lime)]">
    <Icon className="h-5 w-5" /> {children}
  </h2>
);

export default function DeleteAccount() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Delete Your GrowCalc Account";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="ld-root ld-grain relative min-h-screen" data-testid="delete-account-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 border-b border-[var(--ld-line)] bg-[var(--ld-bg)]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <button
            data-testid="back-button"
            onClick={() => navigate("/")}
            className="grid h-10 w-10 place-items-center rounded-full bg-[var(--ld-surface)] text-[var(--ld-text)] transition-colors duration-200 hover:bg-[var(--ld-surface-2)]"
            aria-label="Back to home"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-black leading-tight tracking-wide text-[var(--ld-text)] sm:text-2xl">
              Delete Account
            </h1>
            <p className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--ld-lime)]">
              <Leaf className="h-3.5 w-3.5" /> GrowCalc
            </p>
          </div>
          <img src={LOGO_URL} alt="GrowCalc logo" className="h-10 w-10 rounded-xl object-contain" />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-24 pt-8 sm:px-6">
        {/* Intro */}
        <div className="gc-rise rounded-2xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/70 p-6 backdrop-blur-xl sm:p-9">
          <h2 className="mb-4 text-2xl sm:text-3xl font-black tracking-tight text-[var(--ld-text)]">
            Delete Your GrowCalc Account
          </h2>
          <p className="text-[15px] leading-relaxed text-[var(--ld-text)]">
            This page explains how to permanently delete your GrowCalc account and all data
            associated with it.
          </p>
        </div>

        <div className="mt-8 space-y-9">
          {/* Section 1 — in-app */}
          <section className="gc-rise" data-testid="delete-section-in-app">
            <SectionTitle icon={Trash2}>Delete your account inside the app (fastest)</SectionTitle>
            <ol className="space-y-3 rounded-2xl border border-[var(--ld-lime)]/30 bg-[var(--ld-lime)]/10 p-5 backdrop-blur-xl sm:p-6">
              {STEPS.map((step, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-[var(--ld-text)]">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--ld-lime)] text-xs font-black text-[var(--ld-bg)]">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Section 2 — email */}
          <section className="gc-rise" data-testid="delete-section-email">
            <SectionTitle icon={Mail}>Or request deletion by email</SectionTitle>
            <p className="text-[15px] leading-relaxed text-[var(--ld-text)]">
              If you can no longer access the app, email <MailLink /> from the email address on your
              account with the subject "GrowCalc account deletion request". We will verify ownership
              and complete the deletion within 30 days.
            </p>
          </section>

          {/* Section 3 — what is deleted */}
          <section className="gc-rise" data-testid="delete-section-deleted">
            <SectionTitle icon={ListChecks}>What is deleted</SectionTitle>
            <ul className="space-y-3">
              {DELETED.map((item, i) => (
                <Bullet key={i}>{item}</Bullet>
              ))}
            </ul>
          </section>

          {/* Section 4 — what is kept */}
          <section className="gc-rise" data-testid="delete-section-kept">
            <SectionTitle icon={ShieldCheck}>What is kept, and for how long</SectionTitle>
            <ul className="space-y-3">
              {KEPT.map((item, i) => (
                <Bullet key={i}>{item}</Bullet>
              ))}
            </ul>
          </section>

          {/* Section 5 — reset data */}
          <section className="gc-rise" data-testid="delete-section-reset">
            <SectionTitle icon={RefreshCw}>Delete your data without deleting your account</SectionTitle>
            <div className="rounded-2xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/70 p-5 backdrop-blur-xl sm:p-6">
              <p className="text-[15px] leading-relaxed text-[var(--ld-text)]">
                Prefer a fresh start instead? In the app, go to{" "}
                <span className="font-extrabold text-[var(--ld-lime-bright)]">Settings → Reset Data</span>{" "}
                to erase all of your app data while keeping your account so you can keep using GrowCalc.
              </p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="mt-12 border-t border-[var(--ld-line)] pt-6">
          <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-[var(--ld-muted)]">
            <Mail className="h-4 w-4 shrink-0 text-[var(--ld-lime)]" />
            Questions? Contact us at <MailLink />.
          </p>
        </footer>
      </main>
    </div>
  );
}
